import { useCallback, useEffect, useMemo, useState } from 'react';
import { AutoComplete, Button, Modal, Popconfirm, Progress, Skeleton, Upload, message } from 'antd';
import { UploadFile } from 'antd/lib/upload/interface';
import {
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineExclamationCircle,
  HiOutlineInformationCircle,
  HiOutlineUpload,
} from 'react-icons/hi';
import moment from 'moment';

import { PanelLayout } from '../../components/PanelLayout';
import { useAuth } from '../../hooks/useAuth';
import { useQuery } from '../../hooks/useQuery';
import {
  ExtratoFile,
  ExtratoMonth,
  ExtratoStatus,
  ExtratoYear,
  STATUS_LABELS,
  deleteExtrato,
  downloadExtrato,
  getExtratoYear,
  uploadExtratos,
} from '../../operations/extratoBancario';
import {
  NoticeContainer,
  NoticeContent,
  NoticeIcon,
  NoticeText,
} from '../DividasTributarias/styles';
import {
  Container,
  FieldLabel,
  LinkButton,
  Legend,
  LegendItem,
  MissingBanner,
  ModalNote,
  MonthAction,
  MonthCard,
  MonthFiles,
  MonthName,
  MonthStatus,
  MonthsGrid,
  PageHeader,
  PdfBadge,
  SentItem,
  SentList,
  WhatsappContainer,
  WhatsappLink,
  WhatsappText,
  YearButton,
  YearLabel,
  YearNav,
} from './styles';

const MAX_MB = 10;
const CLOSED: ExtratoStatus[] = ['futuro', 'fora'];

const monthName = (competence: string, withYear = false) => {
  const label = moment(`${competence}-01`).locale('pt-br').format(withYear ? 'MMMM/YYYY' : 'MMMM');
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const statusIcon = (status: ExtratoStatus) => {
  if (status === 'entregue') return <HiOutlineCheckCircle aria-hidden />;
  if (status === 'nao_entregue') return <HiOutlineExclamationCircle aria-hidden />;
  if (status === 'em_conferencia') return <HiOutlineClock aria-hidden />;
  return null;
};

const actionLabel = (month: ExtratoMonth) => {
  if (CLOSED.includes(month.status)) return '';
  if (month.status === 'nao_entregue') return 'Enviar o que falta';
  if (month.status === 'entregue' || month.status === 'sem_movimento') {
    return month.files_count > 0 ? 'Ver arquivos' : '';
  }
  if (month.files_count > 0) return 'Ver / enviar mais';
  return 'Anexar PDF';
};

export const ExtratoBancario = () => {
  const { user } = useAuth();
  const query = useQuery();
  const deepLinkMonth = query.get('mes');

  const currentYear = moment().year();
  const initialYear = deepLinkMonth && /^\d{4}-\d{2}$/.test(deepLinkMonth)
    ? Number(deepLinkMonth.slice(0, 4))
    : currentYear;

  const [year, setYear] = useState(initialYear);
  const [data, setData] = useState<ExtratoYear | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [openCompetence, setOpenCompetence] = useState<string | null>(null);
  const [deepLinkHandled, setDeepLinkHandled] = useState(false);

  const [bank, setBank] = useState('');
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [progress, setProgress] = useState(0);

  const accountingPhone = user && user.tenant_phones && user.tenant_phones['3'];

  const load = useCallback(() => {
    setIsLoading(true);
    return getExtratoYear(year)
      .then((result) => setData(result))
      .catch(() => setData(null))
      .finally(() => setIsLoading(false));
  }, [year]);

  // Recarrega ao trocar de empresa (o token muda, o user também).
  useEffect(() => {
    load();
  }, [load, user]);

  // Link do e-mail de lembrete: /extrato-bancario?mes=2026-09 abre o mês.
  useEffect(() => {
    if (!deepLinkHandled && data && deepLinkMonth) {
      const month = data.months.find((m) => m.competence === deepLinkMonth);
      if (month && !CLOSED.includes(month.status)) {
        setOpenCompetence(month.competence);
      }
      setDeepLinkHandled(true);
    }
  }, [data, deepLinkMonth, deepLinkHandled]);

  const openMonth = useMemo(
    () => (data && openCompetence ? data.months.find((m) => m.competence === openCompetence) || null : null),
    [data, openCompetence]
  );

  const missing = useMemo(
    () => (data ? data.months.filter((m) => m.status === 'nao_entregue') : []),
    [data]
  );

  const closeModal = () => {
    if (isSending) return;
    setOpenCompetence(null);
    setFileList([]);
    setBank('');
    setProgress(0);
  };

  const beforeUpload = (file: File) => {
    const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
    if (!isPdf) {
      message.error(`${file.name} não é um PDF.`);
      return Upload.LIST_IGNORE;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      message.error(`${file.name} passa de ${MAX_MB} MB.`);
      return Upload.LIST_IGNORE;
    }
    // Segura o arquivo: o envio é no botão, junto com o banco.
    return false;
  };

  const send = async () => {
    if (!openMonth || fileList.length === 0) return;

    const files = fileList
      .map((item) => item.originFileObj as File | undefined)
      .filter((file): file is File => !!file);

    setIsSending(true);
    setProgress(0);
    try {
      await uploadExtratos(openMonth.competence, files, bank.trim(), setProgress);
      setFileList([]);
      setBank('');
      await load();
    } catch (error: any) {
      const errors = error && error.response && error.response.data && error.response.data.errors;
      if (errors) {
        const first = Object.keys(errors).map((key) => errors[key][0])[0];
        if (first) message.error(first);
      }
    } finally {
      setIsSending(false);
    }
  };

  const remove = async (file: ExtratoFile) => {
    try {
      await deleteExtrato(file.id);
      await load();
    } catch (error) {
      // a mensagem já sai pelo interceptor da api
    }
  };

  const download = async (file: ExtratoFile) => {
    try {
      const ok = await downloadExtrato(file);
      if (!ok) message.error('Não foi possível baixar o arquivo.');
    } catch (error) {
      message.error('Não foi possível baixar o arquivo.');
    }
  };

  const renderGrid = () => {
    if (isLoading && !data) {
      return (
        <MonthsGrid>
          {Array.from({ length: 12 }).map((_, index) => (
            <Skeleton.Button key={index} active style={{ width: '100%', height: 118, borderRadius: 10 }} />
          ))}
        </MonthsGrid>
      );
    }

    if (!data) {
      return <NoticeText>Não foi possível carregar os extratos. Tente de novo em instantes.</NoticeText>;
    }

    return (
      <MonthsGrid>
        {data.months.map((month) => {
          const closed = CLOSED.includes(month.status);
          return (
            <MonthCard
              key={month.competence}
              type="button"
              $status={month.status}
              disabled={closed}
              onClick={() => setOpenCompetence(month.competence)}
              aria-label={`${monthName(month.competence, true)}: ${STATUS_LABELS[month.status]}`}
            >
              <MonthName>{monthName(month.competence)}</MonthName>
              <MonthStatus>
                {statusIcon(month.status)}
                {STATUS_LABELS[month.status]}
              </MonthStatus>
              <MonthFiles>
                {month.files_count > 0 ? `${month.files_count} ${month.files_count === 1 ? 'PDF enviado' : 'PDFs enviados'}` : ''}
              </MonthFiles>
              <MonthAction $strong={month.status === 'nao_entregue'}>{actionLabel(month)}</MonthAction>
            </MonthCard>
          );
        })}
      </MonthsGrid>
    );
  };

  const renderModal = () => {
    if (!openMonth || !data) return null;

    const bankOptions = data.banks
      .filter((name) => name.toLowerCase().includes(bank.toLowerCase()))
      .map((name) => ({ value: name }));

    return (
      <Modal
        visible
        title={`Extratos de ${monthName(openMonth.competence, true).toLowerCase()}`}
        onCancel={closeModal}
        footer={[
          <Button key="close" onClick={closeModal} disabled={isSending}>
            Fechar
          </Button>,
          <Button
            key="send"
            type="primary"
            icon={<HiOutlineUpload style={{ marginRight: 6, verticalAlign: '-2px' }} />}
            loading={isSending}
            disabled={fileList.length === 0}
            onClick={send}
            style={fileList.length > 0 ? { background: 'var(--primary)', borderColor: 'var(--primary)' } : undefined}
          >
            Enviar ao escritório
          </Button>,
        ]}
        destroyOnClose
      >
        {openMonth.status === 'nao_entregue' && (
          <ModalNote>
            <strong>Recado do escritório:</strong>{' '}
            {openMonth.note || 'ainda faltam extratos deste mês.'}
          </ModalNote>
        )}

        <FieldLabel htmlFor="extrato-banco">
          De qual banco ou aplicação? <span>(opcional)</span>
        </FieldLabel>
        <AutoComplete
          id="extrato-banco"
          value={bank}
          options={bankOptions}
          onChange={setBank}
          placeholder="Ex.: Itaú conta corrente, BB aplicação"
          style={{ width: '100%' }}
          maxLength={80}
        />
        <div style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 16px' }}>
          Se forem de bancos diferentes, envie um banco de cada vez.
        </div>

        <Upload.Dragger
          accept="application/pdf,.pdf"
          multiple
          fileList={fileList}
          beforeUpload={beforeUpload}
          onChange={({ fileList: next }) => setFileList(next.slice(0, 20))}
          disabled={isSending}
        >
          <p className="ant-upload-drag-icon" style={{ fontSize: 34, color: 'var(--primary)' }}>
            <HiOutlineUpload aria-hidden />
          </p>
          <p className="ant-upload-text">Arraste os PDFs aqui ou clique para escolher</p>
          <p className="ant-upload-hint">Só PDF, até {MAX_MB} MB cada. Pode mandar vários de uma vez.</p>
        </Upload.Dragger>

        {isSending && <Progress percent={progress} size="small" style={{ marginTop: 12 }} />}

        {openMonth.files.length > 0 && (
          <SentList>
            <FieldLabel as="div">Já enviados neste mês</FieldLabel>
            {openMonth.files.map((file) => (
              <SentItem key={file.id}>
                <PdfBadge>PDF</PdfBadge>
                <div className="info">
                  <span className="name" title={file.name}>{file.name}</span>
                  <span className="meta">
                    {file.bank ? `${file.bank} · ` : ''}
                    {moment(file.created_at).format('DD/MM HH:mm')}
                    {file.checked ? ' · conferido' : ' · aguardando conferência'}
                  </span>
                </div>
                <LinkButton type="button" onClick={() => download(file)}>Baixar</LinkButton>
                {!file.checked && (
                  <Popconfirm
                    title="Apagar este arquivo?"
                    okText="Apagar"
                    cancelText="Cancelar"
                    onConfirm={() => remove(file)}
                  >
                    <LinkButton type="button" $danger>Excluir</LinkButton>
                  </Popconfirm>
                )}
              </SentItem>
            ))}
          </SentList>
        )}
      </Modal>
    );
  };

  return (
    <PanelLayout>
      <Container>
        <PageHeader>
          <h2>Extrato Bancário</h2>
          <YearNav>
            <YearButton type="button" aria-label="Ano anterior" onClick={() => setYear(year - 1)} disabled={isLoading}>
              ‹
            </YearButton>
            <YearLabel>{year}</YearLabel>
            <YearButton
              type="button"
              aria-label="Próximo ano"
              onClick={() => setYear(year + 1)}
              disabled={isLoading || year >= currentYear}
            >
              ›
            </YearButton>
          </YearNav>
        </PageHeader>

        {data && (
          <NoticeContainer>
            <NoticeIcon>
              <HiOutlineInformationCircle />
            </NoticeIcon>
            <NoticeContent>
              <NoticeText>{data.notice}</NoticeText>
              {data.deadline_day && (
                <NoticeText>
                  Envie até o dia {data.deadline_day} do mês seguinte. Em cada mês, clique para anexar os PDFs.
                </NoticeText>
              )}
            </NoticeContent>
          </NoticeContainer>
        )}

        {missing.length > 0 && (
          <MissingBanner role="alert">
            <div>
              <strong>
                Faltam extratos de {missing.map((m) => monthName(m.competence).toLowerCase()).join(', ')}.
              </strong>
              {missing[0].note && <span className="note">Recado do escritório: {missing[0].note}</span>}
            </div>
            <Button danger type="primary" onClick={() => setOpenCompetence(missing[0].competence)}>
              Enviar agora
            </Button>
          </MissingBanner>
        )}

        <Legend>
          <LegendItem $status="entregue">Entregue</LegendItem>
          <LegendItem $status="nao_entregue">Não entregue</LegendItem>
          <LegendItem $status="em_conferencia">Em conferência</LegendItem>
          <LegendItem $status="sem_movimento">Sem movimento</LegendItem>
        </Legend>

        {renderGrid()}

        {accountingPhone && (
          <WhatsappContainer>
            <WhatsappText>Prefere mandar de outro jeito ou tem dúvidas?</WhatsappText>
            <WhatsappLink href={`https://wa.me/55${accountingPhone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">
              Falar com o Contábil no WhatsApp
            </WhatsappLink>
          </WhatsappContainer>
        )}

        {renderModal()}
      </Container>
    </PanelLayout>
  );
};

export default ExtratoBancario;
